import { ProjectActionContext } from "@components/common/project-actions";
import { SphereIconButton } from "@components/common/sphere-icon-button";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import { ProjectLaunchTarget } from "@custom-types/sdb-company-types";
import { defaultProjectLaunchTargetSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import {
  getOpenProjectHref,
  getOpenProjectTargetName,
  OPEN_PROJECT_TARGET_ATTRIBUTE,
  OpenProjectProps,
  trackOpenProject,
} from "@utils/project-utils";
import { HolobuilderIcon, ViewerIcon } from "@flat-ui";
import { useMemo } from "react";
import { BaseProjectProps } from "@custom-types/project-types";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { ACTION_BTN_SIZE, ACTION_ICON_SIZE } from "@styles/common-styles";

interface Props extends BaseProjectProps {
  /** Context in which the component is being used */
  contextType: ProjectActionContext;
}

/** Renders an icon button to open a project */
export function ProjectOpenButton({
  project,
  contextType,
}: Props): JSX.Element {
  const { trackAsyncEvent } = useTrackEvent();
  const projectLaunchSettings = useAppSelector(
    defaultProjectLaunchTargetSelector
  );

  // Decides the title for the open project button
  const openProjectTitle = useMemo(() => {
    // If it should not show the default app setting, then it should just say "Open project"
    // Since there is only one option to open the project.
    if (!projectLaunchSettings.shouldShowDefaultAppSetting) {
      // For workspaces that are Sphere XG exclusive, saying "Open project in Sphere XG" is redundant
      return "Open project";
    }

    return `Open project in ${getOpenProjectTargetName(
      projectLaunchSettings.defaultOpenProjectTarget
    )}`;
  }, [projectLaunchSettings]);

  /** Triggered to open a project in the Sphere Viewer */
  async function onTrackOpenProject({
    clickType,
  }: Pick<OpenProjectProps, "clickType">): Promise<void> {
    const numberOfMembers =
      "members" in project && project.members
        ? project.members.length
        : undefined;

    await trackOpenProject({
      openFrom: contextType,
      numberOfMembers,
      openTarget: projectLaunchSettings.defaultOpenProjectTarget,
      clickType,
      ...project,
      trackAsyncEvent,
    });
  }

  return (
    <SphereTooltip title={openProjectTitle}>
      <SphereIconButton
        buttonSize={ACTION_BTN_SIZE}
        iconSize={ACTION_ICON_SIZE}
        component={
          projectLaunchSettings.defaultOpenProjectTarget ===
          ProjectLaunchTarget.sphereViewer
            ? ViewerIcon
            : HolobuilderIcon
        }
        href={getOpenProjectHref({
          projectId: project.id,
          openTarget: projectLaunchSettings.defaultOpenProjectTarget,
        })}
        target={OPEN_PROJECT_TARGET_ATTRIBUTE}
        onClick={(event) => {
          // Prevents the propagation of the click event to avoid opening the project details page.
          event.stopPropagation();
          void onTrackOpenProject({ clickType: "left button click" });
        }}
        onAuxClick={() => {
          void onTrackOpenProject({ clickType: "middle button click" });
        }}
        onContextMenu={() => {
          void onTrackOpenProject({ clickType: "open context menu" });
        }}
      />
    </SphereTooltip>
  );
}
